import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { UserPlus, Users, Heart, Briefcase, BookOpen, Package, Calendar, CreditCard, Plus, Save, Eye, ExternalLink, FileText, CheckCircle, RefreshCw, Copy } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

/**
 * Admin → Students section.
 * Pure-presentational shell: data + handlers come through props from AdminDashboard.
 */
export const StudentsSection = ({
  // data
  students, setStudents,
  teachers, allCourses,
  managingStudent,
  classStats, classLogData,
  // dialog open state
  createStudentOpen, setCreateStudentOpen,
  editingProfile, setEditingProfile,
  manageStudentOpen, setManageStudentOpen,
  classLogOpen, setClassLogOpen,
  showRenewal, setShowRenewal,
  // create form
  newStudent, setNewStudent, isCreating, handleCreateStudent, toggleStudentVertical,
  // simple edit form
  profileForm, setProfileForm, savingProfile, handleSaveProfile, toggleProfileVertical,
  // manage dialog (advanced)
  studentProfileEdit, setStudentProfileEdit,
  savingStudentProfile, handleSaveStudentProfile,
  studentMappings, setStudentMappings,
  savingMapping, handleSaveTeacher,
  toggleCourse, handleSaveCourses,
  toggleModule, handleSaveModules,
  classAllocation, setClassAllocation, handleSaveClasses,
  renewalData, setRenewalData, renewalTotalINR, handleRenewal,
  // student row actions
  openStudentManagement, fetchClassLog,
  // constants
  VERTICALS, COUNTRIES, STATE_MAP, AVAILABLE_MODULES, USD_TO_INR,
  MODULES_BY_VERTICAL = {}, modulesForVertical = (v) => AVAILABLE_MODULES,
  mappingVertical = '', setMappingVertical = () => {},
  moduleCatalogItems = [], refetchModuleCatalog = () => {},
  onOpenProfileCard = null,
}) => {
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [resettingCreds, setResettingCreds] = useState(false);
  const [regionFilter, setRegionFilter] = useState('all');

  // Distinct regions present on students (drives the filter dropdown options).
  const regionOptions = React.useMemo(() => {
    const set = new Set();
    (students || []).forEach(s => { if (s.region) set.add(s.region); });
    return Array.from(set).sort();
  }, [students]);

  const filteredStudents = React.useMemo(() => {
    if (regionFilter === 'all') return students || [];
    if (regionFilter === '__none__') return (students || []).filter(s => !s.region);
    return (students || []).filter(s => s.region === regionFilter);
  }, [students, regionFilter]);

  const handleResetSimpleCreds = async () => {
    if (!window.confirm(
      'Reset every student & teacher login to:\n  username = first name (lowercase)\n  password = Firstname@123\n\nContinue?'
    )) return;
    setResettingCreds(true);
    try {
      const res = await api.post('/admin/reset-simple-credentials', {});
      const rows = res.data?.credentials || [];
      // Build a CSV so admin can copy/paste it out
      const csv = 'Role,CITT_ID,Name,Username,Password\n' +
        rows.map(r => `${r.role},${r.citt_id || ''},"${r.name}",${r.username},${r.password}`).join('\n');
      // Trigger a downloadable file
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `simple-credentials-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Reset ${rows.length} credentials · CSV downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reset credentials');
    } finally {
      setResettingCreds(false);
    }
  };

  // Inline module manager state (per-vertical)
  const [showModuleManager, setShowModuleManager] = useState(false);
  const [newModuleName, setNewModuleName] = useState('');
  const [addingModule, setAddingModule] = useState(false);
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [editingModuleName, setEditingModuleName] = useState('');

  const modulesForCurrentVerticalCatalog = () =>
    moduleCatalogItems.filter(it => it.vertical === mappingVertical).sort((a, b) => (a.order || 0) - (b.order || 0));

  const addModule = async () => {
    if (!mappingVertical) { toast.error('Pick a vertical first'); return; }
    if (!newModuleName.trim()) { toast.error('Enter a module name'); return; }
    setAddingModule(true);
    try {
      await api.post('/admin/vertical-modules', { vertical: mappingVertical, module: newModuleName.trim() });
      toast.success(`Added "${newModuleName.trim()}" to ${mappingVertical}`);
      setNewModuleName('');
      await refetchModuleCatalog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add module');
    } finally {
      setAddingModule(false);
    }
  };
  const saveEditingModule = async () => {
    if (!editingModuleId || !editingModuleName.trim()) return;
    try {
      await api.put(`/admin/vertical-modules/${editingModuleId}`, { module: editingModuleName.trim() });
      toast.success('Module renamed');
      setEditingModuleId(null);
      setEditingModuleName('');
      await refetchModuleCatalog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to rename module');
    }
  };
  const deleteModule = async (id, name) => {
    if (!window.confirm(`Delete module "${name}"?`)) return;
    try {
      await api.delete(`/admin/vertical-modules/${id}`);
      toast.success('Module removed');
      await refetchModuleCatalog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete module');
    }
  };

  const handleSyncFromSheet = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.post('/admin/bulk-import/sheet');
      setSyncResult(res.data);
      toast.success(`Synced — ${res.data.students?.created || 0} students, ${res.data.teachers?.total_after_import || 0} teachers total`);
      // Trigger the parent to refetch the list
      if (typeof setStudents === 'function') {
        try {
          const list = await api.get('/admin/students/detailed');
          setStudents(list.data || []);
        } catch { /* refetch is best-effort */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed. Check that the sheet is publicly viewable.');
    } finally {
      setSyncing(false);
    }
  };

  return (
  <>
    <div className="flex items-center justify-between mb-8">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Students</h1>
        <p className="text-slate-500 font-semibold">
          {regionFilter === 'all'
            ? `${students.length} registered students`
            : `${filteredStudents.length} of ${students.length} · filtered by ${regionFilter === '__none__' ? 'no region' : regionFilter}`}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Select value={regionFilter} onValueChange={setRegionFilter}>
          <SelectTrigger className="w-40 h-9 border-slate-200 bg-white" data-testid="region-filter-select">
            <SelectValue placeholder="All Regions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Regions</SelectItem>
            {regionOptions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            {regionOptions.length > 0 && <SelectItem value="__none__">— no region —</SelectItem>}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          className="border-indigo-500 text-indigo-700 hover:bg-indigo-50 font-bold"
          onClick={handleResetSimpleCreds}
          disabled={resettingCreds}
          data-testid="reset-creds-btn"
          title="Set every student/teacher login to firstname / Firstname@123"
        >
          {resettingCreds ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Resetting…</>) : (<>🔑 Reset Simple Creds</>)}
        </Button>
        <Button
          variant="outline"
          className="border-amber-500 text-amber-700 hover:bg-amber-50 font-bold"
          onClick={() => { setSyncResult(null); setSyncOpen(true); }}
          data-testid="sync-from-sheet-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Sync from Sheet
        </Button>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreateStudentOpen(true)} data-testid="add-student-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Add Student
        </Button>
      </div>
    </div>

    {/* Sync-from-Sheet confirm + result dialog */}
    <Dialog open={syncOpen} onOpenChange={(v) => { if (!syncing) setSyncOpen(v); }}>
      <DialogContent className="bg-white border border-slate-200 shadow-xl max-w-lg" data-testid="sync-from-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-700 font-bold text-lg">Sync from Google Sheet</DialogTitle>
          <DialogDescription>
            This will <b>wipe all existing student data</b> (users, classes, XP, curriculum, certificates) and re-import cleanly from the source-of-truth sheet. Teachers are preserved and any new ones get the next available TID.
            <br /><br />
            <b>Do this once per environment (preview + production).</b>
          </DialogDescription>
        </DialogHeader>
        {!syncResult && (
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setSyncOpen(false)} disabled={syncing} data-testid="sync-cancel-btn">Cancel</Button>
            <Button onClick={handleSyncFromSheet} disabled={syncing} className="bg-amber-600 hover:bg-amber-700" data-testid="sync-confirm-btn">
              {syncing ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Syncing…</>) : (<><RefreshCw className="w-4 h-4 mr-2" /> Wipe & Re-import</>)}
            </Button>
          </div>
        )}
        {syncResult && (
          <div className="mt-4 space-y-2 text-sm" data-testid="sync-result-panel">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
              <CheckCircle className="inline w-4 h-4 text-emerald-600 mr-1" />
              <b>Sync complete</b>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>Students imported: <b>{syncResult.students?.created ?? 0}</b></div>
              <div>Enrollments: <b>{syncResult.students?.enrollments ?? 0}</b></div>
              <div>Multi-vertical students: <b>{syncResult.students?.multi_vertical ?? 0}</b></div>
              <div>Teachers total: <b>{syncResult.teachers?.total_after_import ?? 0}</b></div>
            </div>
            <div className="text-xs text-slate-500">Default password for imported users: <code className="bg-slate-100 px-1 rounded">{syncResult.default_password}</code></div>
            <div className="flex justify-end mt-3">
              <Button onClick={() => { setSyncOpen(false); }} data-testid="sync-close-btn">Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>


    {/* Create Student Dialog */}
    <Dialog open={createStudentOpen} onOpenChange={setCreateStudentOpen}>
      <DialogContent className="bg-white border border-slate-200 shadow-xl max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#1E40AF] font-bold text-lg">Add New Student</DialogTitle>
          <DialogDescription className="text-[#64748B]">Create a student profile with linked parent account. Student ID is auto-generated.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleCreateStudent} className="space-y-5 mt-4">
          <div className="bg-[#F0F7FF] rounded-xl p-5 border border-[#3B82F6]/15">
            <h3 className="font-bold text-[#0F172A] text-sm mb-4 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-[#3B82F6]" /> Student Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-[#334155] font-bold">Student Name *</Label>
                <Input value={newStudent.student_name} onChange={e => setNewStudent(p => ({...p, student_name: e.target.value}))} required className="mt-1 bg-white border-slate-200 focus:border-[#3B82F6] focus:ring-[#3B82F6]/20" placeholder="Enter full name" data-testid="new-student-name" />
              </div>
              <div>
                <Label className="text-xs text-[#334155] font-bold">Username *</Label>
                <Input value={newStudent.student_username} onChange={e => setNewStudent(p => ({...p, student_username: e.target.value}))} required className="mt-1 bg-white border-slate-200 focus:border-[#3B82F6] focus:ring-[#3B82F6]/20" placeholder="Login username" data-testid="new-student-username" />
              </div>
              <div>
                <Label className="text-xs text-[#334155] font-bold">Password</Label>
                <Input value={newStudent.student_password} onChange={e => setNewStudent(p => ({...p, student_password: e.target.value}))} placeholder="demo123" className="mt-1 bg-white border-slate-200 focus:border-[#3B82F6]" data-testid="new-student-password" />
              </div>
              <div>
                <Label className="text-xs text-[#334155] font-bold">Grade</Label>
                <Select value={newStudent.grade} onValueChange={v => setNewStudent(p => ({...p, grade: v}))}>
                  <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid="new-student-grade"><SelectValue placeholder="Select grade" /></SelectTrigger>
                  <SelectContent>
                    {['Pre-K', 'KG', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th', 'College'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#334155] font-bold">Country</Label>
                <Select value={newStudent.country} onValueChange={v => setNewStudent(p => ({...p, country: v, state: ''}))}>
                  <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid="new-student-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(c => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#334155] font-bold">State</Label>
                {STATE_MAP[newStudent.country] ? (
                  <Select value={newStudent.state} onValueChange={v => setNewStudent(p => ({...p, state: v}))}>
                    <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid="new-student-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent>
                      {STATE_MAP[newStudent.country].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={newStudent.state} onChange={e => setNewStudent(p => ({...p, state: e.target.value}))} placeholder="Enter state/region" className="mt-1 bg-white border-slate-200 focus:border-[#3B82F6]" data-testid="new-student-state" />
                )}
              </div>
              <div>
                <Label className="text-xs text-[#334155] font-bold">Timezone</Label>
                <Select value={newStudent.timezone} onValueChange={v => setNewStudent(p => ({...p, timezone: v}))}>
                  <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid="new-student-tz"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['IST', 'EST', 'PST', 'GMT', 'SGT', 'CST', 'CET', 'AEST', 'GST', 'AST'].map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#334155] font-bold">Program Name</Label>
                <Input value={newStudent.program_name} onChange={e => setNewStudent(p => ({...p, program_name: e.target.value}))} placeholder="e.g. Scratch, Python" className="mt-1 bg-white border-slate-200 focus:border-[#3B82F6]" data-testid="new-student-program" />
              </div>
              <div>
                <Label className="text-xs text-[#334155] font-bold">Registered Mobile</Label>
                <Input value={newStudent.contact_number} onChange={e => setNewStudent(p => ({...p, contact_number: e.target.value}))} placeholder="+91..." className="mt-1 bg-white border-slate-200 focus:border-[#3B82F6]" data-testid="new-student-phone" />
              </div>
              <div>
                <Label className="text-xs text-[#334155] font-bold">Alternate Mobile</Label>
                <Input value={newStudent.alternate_phone} onChange={e => setNewStudent(p => ({...p, alternate_phone: e.target.value}))} placeholder="+91..." className="mt-1 bg-white border-slate-200 focus:border-[#3B82F6]" data-testid="new-student-alt-phone" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-[#334155] font-bold">Email ID</Label>
                <Input type="email" value={newStudent.student_email} onChange={e => setNewStudent(p => ({...p, student_email: e.target.value}))} placeholder="student@email.com" className="mt-1 bg-white border-slate-200 focus:border-[#3B82F6]" data-testid="new-student-email" />
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-xs text-[#334155] font-bold mb-2 block">Vertical</Label>
              <div className="flex flex-wrap gap-2">
                {VERTICALS.map(v => (
                  <button key={v} type="button" onClick={() => toggleStudentVertical(v)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all ${newStudent.verticals.includes(v) ? 'bg-[#3B82F6] text-white border-[#3B82F6] shadow-sm' : 'bg-white text-[#334155] border-slate-200 hover:border-[#3B82F6]/50'}`}
                    data-testid={`new-student-vertical-${v}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <Button type="submit" className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white font-bold h-11 rounded-xl shadow-sm" disabled={isCreating} data-testid="submit-create-student">
            {isCreating ? 'Creating...' : 'Create Student'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>

    {/* Edit Student Profile Dialog */}
    <Dialog open={!!editingProfile} onOpenChange={(open) => { if (!open) setEditingProfile(null); }}>
      <DialogContent className="bg-white border border-gray-200 shadow-xl max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Profile — {editingProfile?.name}</DialogTitle>
          <DialogDescription>Update student and parent information</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-400/30">
            <h3 className="font-bold text-black text-sm mb-3">Student Info</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-500 font-semibold">Student Name</Label><Input value={profileForm.name} onChange={e => setProfileForm(p => ({...p, name: e.target.value}))} className="mt-1" data-testid="edit-student-name" /></div>
              <div><Label className="text-xs text-slate-500 font-semibold">Grade</Label><Input value={profileForm.grade} onChange={e => setProfileForm(p => ({...p, grade: e.target.value}))} placeholder="e.g. 5th" className="mt-1" data-testid="edit-student-grade" /></div>
              <div><Label className="text-xs text-slate-500 font-semibold">Region</Label><Input value={profileForm.region} onChange={e => setProfileForm(p => ({...p, region: e.target.value}))} className="mt-1" data-testid="edit-student-region" /></div>
              <div><Label className="text-xs text-slate-500 font-semibold">Timezone</Label>
                <Select value={profileForm.timezone} onValueChange={v => setProfileForm(p => ({...p, timezone: v}))}>
                  <SelectTrigger className="mt-1" data-testid="edit-student-tz"><SelectValue placeholder="Select timezone" /></SelectTrigger>
                  <SelectContent>
                    {['IST', 'EST', 'PST', 'GMT', 'SGT', 'CST', 'CET', 'AEST'].map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-500 font-semibold">Contact Number</Label><Input value={profileForm.contact_number} onChange={e => setProfileForm(p => ({...p, contact_number: e.target.value}))} className="mt-1" data-testid="edit-student-phone" /></div>
              <div><Label className="text-xs text-slate-500 font-semibold">Email ID</Label><Input type="email" value={profileForm.student_email} onChange={e => setProfileForm(p => ({...p, student_email: e.target.value}))} className="mt-1" data-testid="edit-student-email" /></div>
            </div>
            <div className="mt-3">
              <Label className="text-xs text-slate-500 font-semibold mb-1 block">Verticals</Label>
              <div className="flex flex-wrap gap-2">
                {VERTICALS.map(v => (
                  <button key={v} type="button" onClick={() => toggleProfileVertical(v)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${profileForm.verticals.includes(v) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-400 font-semibold border-slate-300 hover:border-blue-400'}`}
                    data-testid={`edit-vertical-${v}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-400/30">
            <h3 className="font-bold text-black text-sm mb-3">Parent Contact (optional)</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-500 font-semibold">Parent Name</Label><Input value={profileForm.parent_name} onChange={e => setProfileForm(p => ({...p, parent_name: e.target.value}))} className="mt-1" data-testid="edit-parent-name" /></div>
              <div><Label className="text-xs text-slate-500 font-semibold">Parent Email</Label><Input type="email" value={profileForm.parent_email} onChange={e => setProfileForm(p => ({...p, parent_email: e.target.value}))} className="mt-1" data-testid="edit-parent-email" /></div>
              <div className="col-span-2"><Label className="text-xs text-slate-500 font-semibold">Parent Phone</Label><Input value={profileForm.parent_phone} onChange={e => setProfileForm(p => ({...p, parent_phone: e.target.value}))} className="mt-1" data-testid="edit-parent-phone" /></div>
            </div>
            <p className="text-xs text-slate-500 mt-2">Stored as contact fields only — no separate parent login is created.</p>
          </div>
          <Button onClick={handleSaveProfile} disabled={savingProfile} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="save-profile-btn">
            {savingProfile ? 'Saving...' : 'Save Profile'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Student Management Dialog (Mappings & Classes) */}
    <Dialog open={manageStudentOpen} onOpenChange={setManageStudentOpen}>
      <DialogContent className="bg-white border border-gray-200 shadow-xl max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Student — {managingStudent?.name}</DialogTitle>
          <DialogDescription>{managingStudent?.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 mt-4">

          {/* Student & Parent Profile Edit */}
          <div className="bg-slate-500/10 rounded-xl p-4 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-black flex items-center gap-2"><Users className="w-4 h-4 text-slate-500 font-semibold" /> Student Details</h3>
              <Button size="sm" onClick={handleSaveStudentProfile} disabled={savingStudentProfile} className="bg-slate-700 hover:bg-slate-800 text-xs" data-testid="save-student-profile-btn"><Save className="w-3 h-3 mr-1" /> {savingStudentProfile ? 'Saving...' : 'Save'}</Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Name</Label><Input value={studentProfileEdit.name} onChange={e => setStudentProfileEdit(p => ({...p, name: e.target.value}))} className="mt-1" data-testid="edit-student-name" /></div>
              <div><Label className="text-xs">Email</Label><Input value={studentProfileEdit.student_email} onChange={e => setStudentProfileEdit(p => ({...p, student_email: e.target.value}))} className="mt-1" data-testid="edit-student-email" /></div>
              <div><Label className="text-xs">Grade</Label><Input value={studentProfileEdit.grade} onChange={e => setStudentProfileEdit(p => ({...p, grade: e.target.value}))} className="mt-1" data-testid="edit-student-grade" /></div>
              <div><Label className="text-xs">Contact</Label><Input value={studentProfileEdit.contact_number} onChange={e => setStudentProfileEdit(p => ({...p, contact_number: e.target.value}))} className="mt-1" data-testid="edit-student-contact" /></div>
              <div><Label className="text-xs">Region</Label><Input value={studentProfileEdit.region} onChange={e => setStudentProfileEdit(p => ({...p, region: e.target.value}))} className="mt-1" data-testid="edit-student-region" /></div>
              <div><Label className="text-xs">Timezone</Label>
                <Select value={studentProfileEdit.timezone} onValueChange={v => setStudentProfileEdit(p => ({...p, timezone: v}))}>
                  <SelectTrigger className="mt-1" data-testid="edit-student-tz"><SelectValue /></SelectTrigger>
                  <SelectContent>{['IST','EST','PST','GMT','SGT','CST','CET','AEST'].map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-200">
              <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1"><BookOpen className="w-3 h-3 text-indigo-500" /> Verticals — click to enroll / unenroll, set class counts inline</h4>
              <div className="space-y-2" data-testid="verticals-picker">
                {VERTICALS.map(v => {
                  const enrolled = (studentProfileEdit.verticals || []).find(x => (typeof x === 'string' ? x === v : x?.vertical === v));
                  const on = !!enrolled;
                  const alloc = (typeof enrolled === 'object' && enrolled) ? enrolled : { vertical: v, classes_purchased: 0, classes_consumed: 0 };
                  const setAlloc = (patch) => setStudentProfileEdit(p => ({
                    ...p,
                    verticals: (p.verticals || []).map(x => {
                      const name = typeof x === 'string' ? x : x?.vertical;
                      if (name !== v) return x;
                      const base = typeof x === 'string' ? { vertical: v, classes_purchased: 0, classes_consumed: 0 } : x;
                      return { ...base, ...patch };
                    }),
                  }));
                  return (
                    <div key={v} className={`flex flex-wrap items-center gap-2 rounded-lg border-2 px-2 py-1.5 transition-colors ${on ? 'bg-indigo-50 border-indigo-300' : 'bg-white border-slate-200'}`}>
                      <button
                        type="button"
                        onClick={() => setStudentProfileEdit(p => ({
                          ...p,
                          verticals: on
                            ? (p.verticals || []).filter(x => (typeof x === 'string' ? x : x?.vertical) !== v)
                            : [ ...(p.verticals || []), { vertical: v, classes_purchased: 0, classes_consumed: 0, status: 'Active' } ],
                        }))}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide transition-all border-2 ${on ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-400 hover:text-indigo-700'}`}
                        data-testid={`vertical-pill-${v}`}
                      >
                        {v}
                      </button>
                      {on && (
                        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-700" data-testid={`vertical-alloc-${v}`}>
                          <label className="font-semibold">Purchased</label>
                          <Input type="number" min="0" value={alloc.classes_purchased ?? 0} onChange={e => setAlloc({ classes_purchased: parseInt(e.target.value || '0', 10) })} className="h-7 w-16 text-xs" data-testid={`vertical-purchased-${v}`} />
                          <label className="font-semibold ml-1">Consumed</label>
                          <Input type="number" min="0" value={alloc.classes_consumed ?? 0} onChange={e => setAlloc({ classes_consumed: parseInt(e.target.value || '0', 10) })} className="h-7 w-16 text-xs" data-testid={`vertical-consumed-${v}`} />
                          <label className="font-semibold ml-1">Teacher</label>
                          <Select value={alloc.teacher_id || ''} onValueChange={(val) => {
                            const t = teachers.find(x => x.teacher_id === val || x.id === val);
                            setAlloc({ teacher_id: val, teacher_name: t?.name || alloc.teacher_name || '' });
                          }}>
                            <SelectTrigger className="h-7 w-36 text-xs" data-testid={`vertical-teacher-${v}`}>
                              <SelectValue placeholder={alloc.teacher_name || 'Assign'} />
                            </SelectTrigger>
                            <SelectContent className="max-h-[240px]">
                              {teachers.map(t => (
                                <SelectItem key={t.id} value={t.teacher_id || t.id}>
                                  {t.name} {t.teacher_id ? `(${t.teacher_id})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-500 mt-1.5">Existing class/teacher data is preserved on save. New verticals default to 0 classes.</p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-200">
              <h4 className="text-xs font-semibold text-slate-700 font-semibold mb-2 flex items-center gap-1"><Heart className="w-3 h-3 text-pink-500" /> Parent Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label className="text-xs">Parent Name</Label><Input value={studentProfileEdit.parent_name} onChange={e => setStudentProfileEdit(p => ({...p, parent_name: e.target.value}))} className="mt-1" data-testid="edit-parent-name" /></div>
                <div><Label className="text-xs">Parent Email</Label><Input value={studentProfileEdit.parent_email} onChange={e => setStudentProfileEdit(p => ({...p, parent_email: e.target.value}))} className="mt-1" data-testid="edit-parent-email" /></div>
                <div><Label className="text-xs">Parent Phone</Label><Input value={studentProfileEdit.parent_phone} onChange={e => setStudentProfileEdit(p => ({...p, parent_phone: e.target.value}))} className="mt-1" data-testid="edit-parent-phone" /></div>
              </div>
            </div>
          </div>

          {/* Teacher assignment is now per-vertical (managed on the enrollment rows below).
              The old global "Assign Teacher" section has been removed to prevent
              conflicts for multi-vertical students. */}

          {/* Vertical filter shared by Course + Module Mapping */}
          {(studentProfileEdit.verticals || []).length > 0 && (
            <div className="bg-slate-100 rounded-xl px-4 py-3 border border-slate-200 flex items-center gap-3" data-testid="mapping-vertical-filter">
              <BookOpen className="w-4 h-4 text-slate-500" />
              <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide">Show mappings for</Label>
              <Select value={mappingVertical} onValueChange={setMappingVertical}>
                <SelectTrigger className="h-8 w-52 text-xs" data-testid="mapping-vertical-select">
                  <SelectValue placeholder="Select a vertical" />
                </SelectTrigger>
                <SelectContent>
                  {(studentProfileEdit.verticals || []).map(v => {
                    const name = typeof v === 'string' ? v : v?.vertical;
                    if (!name) return null;
                    return <SelectItem key={name} value={name} data-testid={`mapping-vertical-option-${name}`}>{name}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              <span className="text-[11px] text-slate-500">Course + Module lists below are filtered by this selection.</span>
            </div>
          )}

          {/* Reactivate vertical (visible only when the currently-selected vertical is Inactive) */}
          {(() => {
            const v = (studentProfileEdit.verticals || []).find(x => (typeof x === 'object' ? x?.vertical : x) === mappingVertical);
            const status = typeof v === 'object' ? (v?.status || 'Active') : 'Active';
            if (!v || status === 'Active') return null;
            const reason = typeof v === 'object' ? v?.reason : '';
            const reactivateVertical = async () => {
              if (!window.confirm(`Reactivate "${mappingVertical}" for ${studentProfileEdit.name}?`)) return;
              try {
                await api.post('/admin/reactivate-vertical', {
                  student_id: studentProfileEdit.id, vertical: mappingVertical
                });
                toast.success(`${mappingVertical} reactivated`);
                // Optimistically flip status locally
                setStudentProfileEdit(p => ({
                  ...p,
                  verticals: (p.verticals || []).map(x => {
                    if (typeof x === 'object' && x?.vertical === mappingVertical) {
                      const { reason: _r, ...rest } = x;
                      return { ...rest, status: 'Active' };
                    }
                    return x;
                  })
                }));
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Failed to reactivate');
              }
            };
            return (
              <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="reactivate-banner">
                <div>
                  <p className="text-sm font-bold text-amber-800">{mappingVertical} is currently <span className="uppercase">Inactive</span></p>
                  {reason && <p className="text-xs text-amber-700 mt-0.5">Reason: <b>{reason}</b></p>}
                </div>
                <Button size="sm" onClick={reactivateVertical} className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid="reactivate-btn">
                  ↺ Reactivate
                </Button>
              </div>
            );
          })()}

          {/* Course Mapping */}
          <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-400/30">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-black flex items-center gap-2"><BookOpen className="w-4 h-4 text-emerald-600" /> Course Mapping</h3>
              <Button size="sm" onClick={handleSaveCourses} disabled={savingMapping} className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid="save-courses-btn"><Save className="w-3 h-3 mr-1" /> Save</Button>
            </div>
            {(() => {
              const filteredCourses = mappingVertical
                ? allCourses.filter(c => (c.vertical || c.category || c.level || '').toLowerCase().includes(mappingVertical.toLowerCase())
                                          || (c.title || '').toLowerCase().includes(mappingVertical.toLowerCase()))
                : allCourses;
              if (filteredCourses.length === 0) {
                return <p className="text-sm text-slate-500 font-semibold">No courses available{mappingVertical ? ` in ${mappingVertical}` : ''}</p>;
              }
              return (
                <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                  {filteredCourses.map(course => (
                    <label key={course.id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${studentMappings.assigned_courses.includes(course.id) ? 'bg-emerald-100 border-emerald-400' : 'bg-white border-slate-200 hover:border-emerald-300'}`} data-testid={`course-${course.id}`}>
                      <input type="checkbox" checked={studentMappings.assigned_courses.includes(course.id)} onChange={() => toggleCourse(course.id)} className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-black truncate">{course.title}</p>
                        <p className="text-xs text-slate-500 font-semibold">{course.level} &middot; {course.teacher_name}</p>
                      </div>
                    </label>
                  ))}
                </div>
              );
            })()}
            <p className="text-xs text-emerald-600 mt-2">{studentMappings.assigned_courses.length} course(s) selected</p>
          </div>

          {/* Module Mapping */}
          <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-400/30">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-black flex items-center gap-2"><Package className="w-4 h-4 text-purple-600" /> Module Mapping</h3>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowModuleManager(v => !v)}
                  className="text-xs border-purple-400 text-purple-700 hover:bg-purple-100"
                  data-testid="manage-modules-btn"
                  disabled={!mappingVertical}
                  title={mappingVertical ? `Manage modules for ${mappingVertical}` : 'Pick a vertical first'}
                >
                  {showModuleManager ? 'Close' : 'Manage'}
                </Button>
                <Button size="sm" onClick={handleSaveModules} disabled={savingMapping} className="bg-purple-600 hover:bg-purple-700 text-xs" data-testid="save-modules-btn"><Save className="w-3 h-3 mr-1" /> Save</Button>
              </div>
            </div>

            {/* Inline Module Manager (admin-editable catalog) */}
            {showModuleManager && mappingVertical && (
              <div className="mb-3 rounded-lg border-2 border-purple-300 bg-white p-3 space-y-2" data-testid="module-manager-panel">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-purple-800">Modules under {mappingVertical}</p>
                  <span className="text-[10px] text-slate-500">{modulesForCurrentVerticalCatalog().length} module(s)</span>
                </div>
                <div className="max-h-40 overflow-y-auto divide-y divide-slate-100">
                  {modulesForCurrentVerticalCatalog().map(item => (
                    <div key={item.id} className="flex items-center justify-between gap-2 py-1.5" data-testid={`catalog-row-${item.id}`}>
                      {editingModuleId === item.id ? (
                        <>
                          <Input value={editingModuleName} onChange={e => setEditingModuleName(e.target.value)} className="h-7 text-xs flex-1" data-testid={`catalog-edit-input-${item.id}`} />
                          <Button size="sm" onClick={saveEditingModule} className="h-7 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700" data-testid={`catalog-save-${item.id}`}>Save</Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditingModuleId(null); setEditingModuleName(''); }} className="h-7 text-[10px] px-2" data-testid={`catalog-cancel-${item.id}`}>Cancel</Button>
                        </>
                      ) : (
                        <>
                          <span className="text-xs text-slate-800 font-medium flex-1 truncate">{item.module}</span>
                          <button
                            type="button"
                            onClick={() => { setEditingModuleId(item.id); setEditingModuleName(item.module); }}
                            className="text-[11px] text-slate-600 hover:text-purple-700 font-semibold"
                            data-testid={`catalog-edit-${item.id}`}
                          >Edit</button>
                          <button
                            type="button"
                            onClick={() => deleteModule(item.id, item.module)}
                            className="text-[11px] text-red-600 hover:text-red-800 font-semibold"
                            data-testid={`catalog-delete-${item.id}`}
                          >Delete</button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-purple-100">
                  <Input
                    value={newModuleName}
                    onChange={e => setNewModuleName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addModule(); } }}
                    placeholder={`New module under ${mappingVertical}…`}
                    className="h-8 text-xs"
                    data-testid="new-module-input"
                  />
                  <Button size="sm" onClick={addModule} disabled={addingModule || !newModuleName.trim()} className="h-8 text-xs bg-purple-600 hover:bg-purple-700" data-testid="add-module-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              {modulesForVertical(mappingVertical).map(mod => (
                <label key={mod} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all ${studentMappings.assigned_modules.includes(mod) ? 'bg-purple-100 border-purple-400' : 'bg-white border-slate-200 hover:border-purple-300'}`} data-testid={`module-${mod}`}>
                  <input type="checkbox" checked={studentMappings.assigned_modules.includes(mod)} onChange={() => toggleModule(mod)} className="rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                  <span className="text-sm text-slate-700 font-semibold">{mod}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-purple-600 mt-2">{studentMappings.assigned_modules.length} module(s) selected</p>
          </div>

          {/* Class Allocation */}
          <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-400/30">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-black flex items-center gap-2"><Calendar className="w-4 h-4 text-amber-600" /> Class Allocation</h3>
              <Button size="sm" onClick={handleSaveClasses} disabled={savingMapping} className="bg-amber-600 hover:bg-amber-700 text-xs" data-testid="save-classes-btn"><Save className="w-3 h-3 mr-1" /> Save</Button>
            </div>
            <div className="flex items-center gap-3">
              <Input type="number" min="0" value={classAllocation} onChange={e => setClassAllocation(parseInt(e.target.value) || 0)} className="w-32" data-testid="class-allocation-input" />
              <span className="text-sm text-slate-500 font-semibold">total classes</span>
            </div>
            {classStats && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-center">
                <div className="glass-card rounded-lg p-2 border"><div className="text-lg font-bold text-emerald-600">{classStats.completed}</div><div className="text-[10px] text-slate-500 font-semibold">Completed</div></div>
                <div className="glass-card rounded-lg p-2 border"><div className="text-lg font-bold text-amber-600">{classStats.remaining}</div><div className="text-[10px] text-slate-500 font-semibold">Remaining</div></div>
                <div className="glass-card rounded-lg p-2 border"><div className="text-lg font-bold text-slate-700 font-semibold">{classStats.total_assigned}</div><div className="text-[10px] text-slate-500 font-semibold">Total</div></div>
              </div>
            )}
            {classStats?.needs_renewal && !showRenewal && (
              <Button variant="outline" onClick={() => setShowRenewal(true)} className="w-full mt-3 border-red-300 text-red-600 hover:bg-red-500/10" data-testid="renewal-btn">
                <Plus className="w-3 h-3 mr-1" /> Renew Classes (Mark as Paid)
              </Button>
            )}
            {!classStats?.needs_renewal && !showRenewal && (
              <Button variant="outline" onClick={() => setShowRenewal(true)} className="w-full mt-3 border-amber-300 text-amber-600 hover:bg-amber-500/10" data-testid="add-classes-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Classes & Record Payment
              </Button>
            )}
          </div>

          {/* Renewal Form */}
          {showRenewal && (
            <div className="bg-red-500/10 rounded-xl p-4 border border-red-400/30">
              <h3 className="font-bold text-black flex items-center gap-2 mb-3"><CreditCard className="w-4 h-4 text-red-600" /> Renewal / Add Classes</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-500 font-semibold">Classes to Add</label><Input type="number" min="1" value={renewalData.classes_to_add} onChange={e => setRenewalData(p => ({...p, classes_to_add: parseInt(e.target.value) || 0}))} data-testid="renewal-classes" /></div>
                <div><label className="text-xs text-slate-500 font-semibold">Currency</label>
                  <Select value={renewalData.currency} onValueChange={v => setRenewalData(p => ({...p, currency: v}))}>
                    <SelectTrigger data-testid="renewal-currency"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="INR">INR (₹)</SelectItem><SelectItem value="USD">USD ($)</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><label className="text-xs text-slate-500 font-semibold">Payment Received ({renewalData.currency === 'INR' ? '₹' : '$'})</label><Input type="number" min="0" step="0.01" value={renewalData.payment_received} onChange={e => setRenewalData(p => ({...p, payment_received: parseFloat(e.target.value) || 0}))} data-testid="renewal-payment" /></div>
                <div><label className="text-xs text-slate-500 font-semibold">Discount (₹)</label><Input type="number" min="0" step="0.01" value={renewalData.discount} onChange={e => setRenewalData(p => ({...p, discount: parseFloat(e.target.value) || 0}))} data-testid="renewal-discount" /></div>
              </div>
              <div className="mt-3">
                <label className="text-xs text-slate-500 font-semibold mb-1 block">Payment Method</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'razorpay_card', label: 'Card (Razorpay)' },
                    { id: 'razorpay_upi', label: 'UPI (Razorpay)' },
                    { id: 'paypal', label: 'PayPal' },
                    { id: 'bank_transfer', label: 'Bank Transfer' },
                  ].map(m => (
                    <button key={m.id} onClick={() => setRenewalData(p => ({...p, payment_method: m.id}))}
                      className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${renewalData.payment_method === m.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-400 font-semibold border-slate-200 hover:border-blue-400'}`}
                      data-testid={`renewal-method-${m.id}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-3 bg-white rounded-lg p-3 border text-center">
                <p className="text-xs text-slate-500 font-semibold">Total Payment (INR)</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="renewal-total">₹{renewalTotalINR.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                {renewalData.currency === 'USD' && <p className="text-xs text-slate-500 font-semibold">@ ₹{USD_TO_INR}/USD</p>}
              </div>
              <div className="flex gap-2 mt-3">
                <Button onClick={handleRenewal} disabled={savingMapping} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-renewal-btn">Mark as Paid & Add Classes</Button>
                <Button variant="outline" onClick={() => setShowRenewal(false)} className="flex-shrink-0">Cancel</Button>
              </div>
            </div>
          )}

        </div>
      </DialogContent>
    </Dialog>

    {/* Students Table */}
    <div className="glass-card rounded-xl overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-blue-500/10">
            <TableHead className="text-xs text-blue-700 font-medium">Student ID</TableHead>
            <TableHead className="text-xs text-blue-700 font-medium">Student Name</TableHead>
            <TableHead className="text-xs text-blue-700 font-medium">Parent Name</TableHead>
            <TableHead className="text-xs text-blue-700 font-medium">Grade</TableHead>
            <TableHead className="text-xs text-blue-700 font-medium">Region</TableHead>
            <TableHead className="text-xs text-blue-700 font-medium">Vertical</TableHead>
            <TableHead className="text-xs text-blue-700 font-medium">Teacher</TableHead>
            <TableHead className="text-xs text-blue-700 font-medium">Contact</TableHead>
            <TableHead className="text-xs text-blue-700 font-medium text-center">Progress</TableHead>
            <TableHead className="text-xs text-blue-700 font-medium text-center">Status</TableHead>
            <TableHead className="text-xs text-blue-700 font-medium text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredStudents.map((student) => (
            <TableRow key={student.id} className={`hover:bg-slate-500/10 ${student.is_active === false ? 'opacity-60' : ''}`} data-testid={`student-row-${student.id}`}>
              <TableCell className="text-xs text-slate-700 font-medium">
                <span className="font-mono font-bold text-slate-900" data-testid={`student-id-${student.id}`}>{student.citt_id || '—'}</span>
              </TableCell>
              <TableCell className="text-xs">
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => onOpenProfileCard && onOpenProfileCard(student.id)}
                    className="text-slate-900 hover:text-blue-700 hover:underline font-medium"
                    data-testid={`open-profile-${student.id}`}
                  >{student.name}</button>
                  {student.category === 'demo' ? (
                    <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded uppercase" data-testid={`category-demo-${student.id}`}>Demo</span>
                  ) : (
                    <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] rounded uppercase" data-testid={`category-paid-${student.id}`}>Paid</span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-xs">
                {student.parent_name ? (
                  <span className="text-amber-700">{student.parent_name}</span>
                ) : <span className="text-slate-400">Not linked</span>}
              </TableCell>
              <TableCell className="text-xs text-slate-700">{student.grade || '-'}</TableCell>
              <TableCell className="text-xs">
                <div className="text-slate-700">{student.region || '-'}</div>
                <Select value={student.timezone || ''} onValueChange={async (v) => {
                  try {
                    await api.put(`/admin/students/${student.id}/profile`, { timezone: v });
                    setStudents(prev => prev.map(s => s.id === student.id ? {...s, timezone: v} : s));
                    toast.success(`Timezone updated to ${v}`);
                  } catch { toast.error('Failed to update timezone'); }
                }}>
                  <SelectTrigger className="h-6 mt-0.5 w-20 text-[10px] border-slate-200" data-testid={`tz-select-${student.id}`}>
                    <SelectValue placeholder="TZ" />
                  </SelectTrigger>
                  <SelectContent>
                    {['IST', 'EST', 'PST', 'CST', 'GMT', 'SGT', 'CET', 'AEST', 'JST', 'KST', 'MST', 'UTC'].map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="text-xs">
                {(student.verticals || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {student.verticals.map((v, i) => {
                      const label = typeof v === 'string' ? v : (v?.vertical || '');
                      if (!label) return null;
                      return (
                        <span key={i} className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-[10px] rounded-full">{label}</span>
                      );
                    })}
                  </div>
                ) : <span className="text-slate-400">-</span>}
              </TableCell>
              <TableCell className="text-xs">
                {student.mapped_teacher_id ? (
                  <span
                    className="text-blue-600 cursor-help underline decoration-dotted"
                    title={student.mapped_teacher_name || 'Teacher'}
                    data-testid={`teacher-tid-${student.id}`}
                  >
                    {student.mapped_teacher_tid || student.mapped_teacher_name || 'Mapped'}
                  </span>
                ) : (
                  <span className="text-slate-400">Not assigned</span>
                )}
              </TableCell>
              <TableCell className="text-xs text-slate-600">
                {student.contact_number || '-'}
              </TableCell>
              <TableCell className="text-center" data-testid={`admin-progress-${student.id}`}>
                {(() => {
                  const purchased = student.class_credits?.total_purchased || student.total_purchased || 0;
                  const done = student.class_credits?.classes_completed || student.present_total || 0;
                  if (!purchased && !done) return <span className="text-slate-300 text-xs">—</span>;
                  const pct = purchased > 0 ? Math.round((done / purchased) * 100) : 0;
                  return (
                    <div className="inline-flex flex-col items-center min-w-[70px]">
                      <span className="text-sm font-bold text-emerald-700 leading-tight">{done}<span className="text-slate-400">/{purchased}</span></span>
                      <div className="w-14 h-1 bg-slate-100 rounded-full mt-1 overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                    </div>
                  );
                })()}
              </TableCell>
              <TableCell className="text-center">
                <button
                  onClick={async () => {
                    const next = !(student.is_active !== false);
                    try {
                      await api.put(`/admin/students/${student.id}/status`, { is_active: next });
                      setStudents(prev => prev.map(s => s.id === student.id ? { ...s, is_active: next } : s));
                      toast.success(`Marked ${next ? 'Active' : 'Inactive'}`);
                    } catch { toast.error('Failed to update status'); }
                  }}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition ${student.is_active !== false ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                  data-testid={`status-toggle-${student.id}`}
                  title="Click to toggle"
                >
                  {student.is_active !== false ? 'Active' : 'Inactive'}
                </button>
              </TableCell>
              <TableCell className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-500/10 h-7 px-2 text-[11px]"
                    onClick={() => openStudentManagement(student)}
                    data-testid={`profile-${student.id}`}
                    title="Edit profile, mappings & classes"
                  >
                    <Users className="w-3.5 h-3.5 mr-1" /> Profile
                  </Button>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2 rounded-md"
                    onClick={async () => {
                      try {
                        const res = await api.post(`/admin/impersonate/${student.id}`);
                        const { access_token, user } = res.data;
                        const url = `/impersonate?token=${encodeURIComponent(access_token)}&role=student&user=${encodeURIComponent(JSON.stringify(user))}`;
                        window.open(url, '_blank', 'noopener,noreferrer');
                        toast.success(`Opened ${student.name}'s view in a new tab`);
                      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to impersonate'); }
                    }}
                    data-testid={`impersonate-student-${student.id}`}
                    title="View as this student (opens new tab)"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-teal-600 hover:text-teal-800 hover:bg-teal-500/10 h-7 w-7 p-0"
                    onClick={() => { const link = document.createElement('a'); link.href = `${process.env.REACT_APP_BACKEND_URL}/api/admin/student-progress/${student.id}/export`; link.download = `progress_${student.name}.csv`; link.click(); }}
                    data-testid={`export-progress-${student.id}`}
                    title="Export progress to CSV"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-500/10 h-7 w-7 p-0"
                    onClick={() => fetchClassLog(student.id)}
                    data-testid={`class-log-${student.id}`}
                    title="View Class Log"
                  >
                    <FileText className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>

    {/* Class Log Dialog */}
    <Dialog open={classLogOpen} onOpenChange={setClassLogOpen}>
      <DialogContent className="bg-white max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Class Log - {classLogData?.student_name}</DialogTitle>
          <DialogDescription>Class usage overview and history</DialogDescription>
        </DialogHeader>
        {classLogData && (
          <div className="space-y-4 mt-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-blue-500/10 rounded-xl p-3 text-center border border-blue-400/30">
                <p className="text-xl font-bold text-blue-600 font-bold" data-testid="classes-bought">{classLogData.classes_bought}</p>
                <p className="text-[10px] text-blue-600 font-medium">Total</p>
              </div>
              <div className="bg-emerald-500/10 rounded-xl p-3 text-center border border-emerald-400/30">
                <p className="text-xl font-bold text-emerald-700" data-testid="classes-consumed">{classLogData.classes_consumed}</p>
                <p className="text-[10px] text-emerald-600 font-medium">Consumed</p>
              </div>
              <div className="bg-amber-500/10 rounded-xl p-3 text-center border border-amber-400/30">
                <p className="text-xl font-bold text-amber-700" data-testid="classes-remaining">{classLogData.classes_remaining}</p>
                <p className="text-[10px] text-amber-600 font-medium">Remaining</p>
              </div>
              <div className="bg-red-500/10 rounded-xl p-3 text-center border border-red-400/30">
                <p className="text-xl font-bold text-red-600" data-testid="classes-cancelled">{classLogData.cancelled || 0}</p>
                <p className="text-[10px] text-red-500 font-medium">Cancelled</p>
              </div>
            </div>

            {classLogData.curriculum_completed?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-emerald-700 mb-2">Classes Consumed</h4>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {classLogData.curriculum_completed.map((cls, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-100 text-xs">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                        <span className="font-bold">Class {cls.class_number}</span>
                        <span className="text-slate-500 font-semibold">{cls.title}</span>
                      </div>
                      <span className="text-emerald-600 font-medium">{cls.course || 'N/A'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="text-sm font-bold text-slate-700 font-semibold mb-2">Full Schedule</h4>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {classLogData.class_history?.map((cls, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-slate-500/10 border border-black/10 text-xs">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${cls.status === 'completed' ? 'bg-emerald-500/100' : cls.status === 'cancelled' ? 'bg-red-400' : 'bg-blue-400'}`} />
                      <span className="font-medium">{cls.date}</span>
                      <span className="text-slate-500 font-semibold">{cls.time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 font-semibold">{cls.teacher_name}</span>
                      <span className={`px-2 py-0.5 rounded-full font-semibold ${cls.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : cls.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-600 font-bold'}`}>
                        {cls.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  </>
  );
};

export default StudentsSection;
